import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/news';
import { todayText } from '@/utils/format';

const navItems = [
  { name: '首页', path: '/' },
  ...categories.map((c) => ({ name: c.name, path: `/category/${c.id}` })),
  { name: '关于我们', path: '/about' },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword('');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="hidden bg-primary-950 text-background-100 md:block">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between px-4 py-1.5 text-xs md:px-6">
          <span className="font-label tracking-wide">{todayText()}</span>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5 text-background-200">
              <i className="ri-shield-check-line text-accent-400"></i>
              权威 · 及时 · 多角度
            </span>
            <Link
              to="/about"
              className="transition-colors hover:text-accent-400"
            >
              联系我们
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-primary-900">
        <div className="mx-auto flex max-w-[1280px] items-center gap-4 px-4 py-3 md:px-6 md:py-4">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-primary-950">
              <i className="ri-flashlight-fill text-2xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-extrabold tracking-tight text-background-50 md:text-2xl">
                911<span className="text-accent-400">爆料网</span>
              </span>
              <span className="mt-1 font-label text-[10px] uppercase tracking-[0.2em] text-background-300">
                NEWS · TIPS · INSIGHT
              </span>
            </span>
          </Link>

          <form
            onSubmit={handleSearch}
            className="ml-auto hidden min-w-0 flex-1 max-w-md items-center rounded-full bg-background-50 px-4 py-2 lg:flex"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索爆料、热点、民生事件"
              className="mx-2 min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="搜索新闻"
            />
            <button
              type="submit"
              className="shrink-0 rounded-full bg-primary-700 px-4 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="ml-auto flex items-center gap-3 lg:ml-0">
            <Link
              to="/submit"
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 sm:flex"
            >
              <i className="ri-megaphone-line"></i>
              我要爆料
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-800 text-background-50 lg:hidden"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
            >
              <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
            </button>
          </div>
        </div>

        <nav className="hidden border-t border-primary-800/70 lg:block">
          <div className="mx-auto flex max-w-[1280px] items-center gap-1 px-4 md:px-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-accent-400'
                    : 'text-background-200 hover:text-background-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </nav>
      </div>

      {menuOpen && (
        <div className="animate-slide-down border-t border-primary-800 bg-primary-900 lg:hidden">
          <div className="px-4 py-3 md:px-6">
            <form
              onSubmit={handleSearch}
              className="flex items-center rounded-full bg-background-50 px-4 py-2"
              role="search"
            >
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索爆料、热点、民生事件"
                className="mx-2 min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="搜索新闻"
              />
              <button
                type="submit"
                className="shrink-0 text-sm font-medium text-primary-700"
              >
                搜索
              </button>
            </form>
          </div>
          <div className="grid grid-cols-2 gap-2 px-4 pb-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary-800 text-accent-400'
                    : 'bg-primary-800/50 text-background-200'
                }`}
              >
                <i className="ri-arrow-right-s-line text-accent-400"></i>
                {item.name}
              </Link>
            ))}
            <Link
              to="/submit"
              className="col-span-2 flex items-center justify-center gap-2 rounded-md bg-accent-500 px-3 py-3 text-sm font-semibold text-primary-950"
            >
              <i className="ri-megaphone-line"></i>
              我要爆料
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}